import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as Highcharts from 'highcharts';
import PatternFill from 'highcharts/modules/pattern-fill';
import { HighchartsChartModule } from 'highcharts-angular';

PatternFill(Highcharts);
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { DropdownMenuComponent, LoaderModule } from '@mds/ui';
import { ForecastChartResponse, ForecastScenario, ForecastScenarioItem, ForecastTimeline } from '@mds/common';
import { CreateProductDialogComponent } from '@dialogs/create-product-dialog/create-product-dialog.component';
import { Scope3Service } from '../../services/scope-3.service';
import { FORECAST_CHART_OPTIONS } from '../../utils/chart-options';
import { formatBigNumber } from '../../utils/constants';
import { ButtonComponent } from '@mds/ui';
import { EmptyChartComponent } from '@pages/dashboards/shared/empty-chart/empty-chart.component';
import { ForecastKpiCardsComponent, ForecastKpiWidget } from '../forecast-kpi-cards/forecast-kpi-cards.component';
import { ForecastLegendComponent } from '../forecast-legend/forecast-legend.component';

const MOCK_FORECAST_RESPONSE: ForecastChartResponse = {
  scenario: {
    rid: '#mock:1',
    name: 'Net Zero 2045',
    status: 'Active',
    baseline_year: 2023,
    baseline_activity: 100.0,
    baseline_emissions: 135.0,
    functional_unit: 'tonnes',
    site_name: 'Apple store',
    site_rid: '#mock:site1'
  },
  timeline: [
    { year: 2023, activity: 100, bau_impact: 140, target_impact: 140, actual_impact: 140, actions: [] },
    { year: 2024, activity: 102, bau_impact: 145, target_impact: 120, actual_impact: 110, actions: [] },
    { year: 2025, activity: 104, bau_impact: 150, target_impact: 120, actual_impact: null, actions: [] },
    { year: 2026, activity: 106, bau_impact: 155, target_impact: 115, actual_impact: null, actions: [] },
    { year: 2027, activity: 108, bau_impact: 160, target_impact: 80, actual_impact: null, actions: [] },
    { year: 2028, activity: 110, bau_impact: 165, target_impact: 75, actual_impact: null, actions: [] },
    { year: 2029, activity: 112, bau_impact: 170, target_impact: 70, actual_impact: null, actions: [] },
    { year: 2030, activity: 114, bau_impact: 175, target_impact: 60, actual_impact: null, actions: [] },
    { year: 2031, activity: 116, bau_impact: 180, target_impact: 50, actual_impact: null, actions: [] },
    { year: 2032, activity: 118, bau_impact: 175, target_impact: 40, actual_impact: null, actions: [] }
  ]
};

@Component({
  selector: 'app-forecast-charts',
  standalone: true,
  imports: [CommonModule, HighchartsChartModule, MdsIconsModule, LoaderModule, ButtonComponent, DropdownMenuComponent, EmptyChartComponent, ForecastKpiCardsComponent, ForecastLegendComponent],
  templateUrl: './forecast-charts.component.html',
  styleUrls: ['./forecast-charts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForecastChartsComponent implements OnInit {
  Highcharts = Highcharts;
  forecastChartOptions: Highcharts.Options;
  loading = true;
  kpiWidgets: ForecastKpiWidget[] = [];
  scenario: ForecastScenario | null = null;
  useMockData = false; // Toggle for mock vs real API

  scenarioOptions: { text: string; value: string }[] = [];
  selectedScenario: { text: string; value: string } | null = null;

  private scope3Service = inject(Scope3Service);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  _filters: FormGroup;

  get filters() {
    return this._filters;
  }

  @Input() set filters(values: FormGroup) {
    this._filters = values;
    this.cdr.markForCheck();
  }

  get commonFilters() {
    const filters = this.filters.getRawValue();
    return {
      receiver_site_rids: filters.buyerSite?.map((c: any) => c.value) || []
    };
  }

  ngOnInit(): void {
    this.loadScenarios();
    this.subscribeToFilterChanges();
  }

  onScenarioChange(selected: any): void {
    const selectedItem = selected?.[0] ?? null;
    if (selectedItem?.value && selectedItem.value !== this.selectedScenario?.value) {
      this.selectedScenario = selectedItem;
      this.loadForecastChart();
    }
  }

  private loadScenarios(): void {
    const siteRids = this.commonFilters.receiver_site_rids;
    const payload: any = {};
    if (siteRids.length) {
      payload.receiver_site_rids = siteRids;
    }
    this.scope3Service
      .getScenarios(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.scenarioOptions = response.results.map((s) => ({
          text: s.name,
          value: s.rid
        }));

        const defaultScenario = response.results.find((s) => s.default);
        const scenarioToSelect = defaultScenario ?? response.results[0];
        if (scenarioToSelect) {
          this.selectedScenario = { text: scenarioToSelect.name, value: scenarioToSelect.rid };
        }

        this.loadForecastChart();
        this.cdr.markForCheck();
      });
  }

  private subscribeToFilterChanges(): void {
    this.filters.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.loadScenarios();
      });
  }

  private loadForecastChart(): void {
    if (!this.selectedScenario?.value) {
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    if (this.useMockData) {
      this.handleForecastResponse(MOCK_FORECAST_RESPONSE);
      return;
    }

    const payload: any = {
      scenario_rid: this.selectedScenario.value
    };
    const siteRids = this.commonFilters.receiver_site_rids;
    if (siteRids.length) {
      payload.receiver_site_rids = siteRids;
    }
    this.scope3Service
      .getForecastChart(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.handleForecastResponse(response);
      });
  }

  private handleForecastResponse(response: ForecastChartResponse): void {
    if (response?.scenario) {
      this.scenario = response.scenario;
      this.buildKpiWidgets(response.scenario, response.timeline);
      this.buildForecastChart(response.timeline, response.scenario);
    }
    this.loading = false;
    this.cdr.markForCheck();
  }

  onCreateScenario(): void {
    const dialogRef = this.dialog.open(CreateProductDialogComponent, {
      autoFocus: false,
      width: '60vw',
      data: {
        dataCard: {
          tool: 'composer/new/scenario_forecast',
          app_name: 'Forecast Scenario'
        }
      }
    });

    dialogRef
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe(() => {
        // Re-trigger data fetch after scenario creation
        this.filters.updateValueAndValidity();
      });
  }

  private buildKpiWidgets(scenario: ForecastScenario, timeline: ForecastTimeline[]): void {
    const lastTimelineEntry = timeline[timeline.length - 1];
    const targetYear = lastTimelineEntry?.year ?? scenario.baseline_year;
    const totalReduction =
      scenario.baseline_emissions != null && lastTimelineEntry
        ? scenario.baseline_emissions - lastTimelineEntry.target_impact
        : 0;

    this.kpiWidgets = [
      {
        icon: MdsIcon.SKIP_NEXT,
        title: 'Forecast',
        value: scenario.name,
        subtitle: '0% CO2 emissions'
      },
      {
        icon: MdsIcon.VERTICAL_ALIGN_BUTTOM,
        title: 'Total reduction',
        value: formatBigNumber(totalReduction),
        unit: 't CO\u2082 eq',
        subtitle: `By ${targetYear}`
      },
      {
        icon: MdsIcon.FACTORY,
        title: 'Site',
        value: scenario.site_name,
        subtitle: scenario.site_name
      }
    ];
  }

  private buildForecastChart(timeline: ForecastTimeline[], scenario: ForecastScenario): void {
    const categories = timeline.map((t) => t.year.toString());
    const baselineEmissions = scenario.baseline_emissions;

    const actualData = timeline.map((t) => {
      if (t.actual_impact == null) return null;
      // When actual === bau, use #003C4F (dark navy)
      const color = t.actual_impact === t.bau_impact ? '#003C4F' : '#339095';
      return { y: t.actual_impact, color, borderColor: '#003C4F' };
    });
    const targetData = timeline.map((t) => t.target_impact);
    const bauData = timeline.map((t) => t.bau_impact);

    const options = structuredClone(FORECAST_CHART_OPTIONS);

    options.xAxis = {
      ...options.xAxis,
      categories,
      min: 0,
      max: categories.length - 1
    };

    // Compute total reduction (area between BAU and target)
    const reductionData = timeline.map((t) =>
      t.bau_impact != null ? Math.max(0, t.bau_impact - t.target_impact) : null
    );

    options.series = [
      // BAU column — hatched pattern, widest layer (behind)
      {
        name: 'Emissions business as usual + growth',
        type: 'column',
        data: bauData,
        color: {
          pattern: {
            path: {
              d: 'M 0 0 L 10 10 M -1 9 L 1 11 M 9 -1 L 11 1',
              strokeWidth: 1,
              stroke: '#9EA3A5'
            },
            width: 10,
            height: 10,
            backgroundColor: 'transparent',
            opacity: 1
          }
        } as any,
        borderColor: '#9EA3A5',
        borderWidth: 1,
        pointPadding: 0.15,
        groupPadding: 0.15
      },
      // Projected emissions column — light teal
      {
        name: 'Projected emissions',
        type: 'column',
        data: targetData,
        color: '#B0D4D6',
        borderColor: '#003C4F',
        borderWidth: 1,
        pointPadding: 0.15,
        groupPadding: 0.15
      },
      // Final emissions column — dark teal (actual historical)
      {
        name: 'Final emissions',
        type: 'column',
        data: actualData,
        color: '#339095',
        borderColor: '#003C4F',
        borderWidth: 1,
        pointPadding: 0.15,
        groupPadding: 0.15
      },

      // Baseline emissions — 1px dashed #FFA76B, on top of all, from start of first bar to end of last bar
      ...(baselineEmissions != null
        ? [
            {
              name: 'Baseline emissions',
              type: 'line' as const,
              data: [
                {
                  x: -0.5,
                  y: baselineEmissions,
                  marker: { enabled: true, radius: 4, fillColor: '#FFA76B', lineWidth: 0, symbol: 'circle' as const }
                },
                {
                  x: timeline.length - 0.5,
                  y: baselineEmissions,
                  marker: { enabled: true, radius: 4, fillColor: '#FFA76B', lineWidth: 0, symbol: 'circle' as const }
                }
              ],
              color: '#FFA76B',
              lineWidth: 1,
              dashStyle: 'Dash' as Highcharts.DashStyleValue,
              enableMouseTracking: false,
              zIndex: 10
            }
          ]
        : []),
      // BAU line — 1px solid #547080
      {
        name: 'Business as usual + growth',
        type: 'line',
        data: bauData,
        color: '#547080',
        lineWidth: 1,
        marker: { enabled: false },
        enableMouseTracking: false,
        zIndex: 3
      }
    ];

    this.forecastChartOptions = options;
  }
}
